import type * as THREE from 'three'

/**
 * InstancedMesh GPU 下标到稳定 occurrence/source Mesh 的不可变快照。
 *
 * GPU 下标会随实例组重建而变化，因此所有查询必须同时依赖当前 Unit 持有的快照；旧快照
 * 永不原地修改，避免隐藏、迁组或重建后把旧射线命中解释成另一个 occurrence。
 */
export class ImportedModelInstanceIndexSnapshot {
  /** GPU 下标到不可变记录的顺序表。 */
  private readonly entries: readonly PuppyCAD.ImportedModelInstanceIndexEntry[]

  /** 稳定 occurrence 身份到 GPU 下标的反向索引。 */
  private readonly instanceIndexByOccurrenceId = new Map<string, number>()

  /**
   * 创建不可变下标快照。
   *
   * @param members 当前实例组按确定顺序排列的成员。
   * @param generation 当前实例组结构代次。
   */
  public constructor(
    members: readonly PuppyCAD.ImportedModelInstancingSlotMember[],
    private readonly generation: number,
  ) {
    const entries = members.map((member, instanceIndex) => {
      if (this.instanceIndexByOccurrenceId.has(member.occurrenceId)) {
        throw new Error(`实例化下标快照 occurrence 重复：${member.occurrenceId}`)
      }
      this.instanceIndexByOccurrenceId.set(member.occurrenceId, instanceIndex)
      return Object.freeze({
        instanceIndex,
        occurrenceId: member.occurrenceId,
        sourceMesh: member.sourceMesh,
      })
    })
    this.entries = Object.freeze(entries)
  }

  /**
   * 按 GPU 下标读取当前 occurrence 记录。
   *
   * @param instanceIndex Three.js 射线命中的瞬时下标。
   * @returns 当前代次对应记录；越界时返回 `null`。
   */
  public resolveEntry(instanceIndex: number): PuppyCAD.ImportedModelInstanceIndexEntry | null {
    if (!Number.isInteger(instanceIndex) || instanceIndex < 0 || instanceIndex >= this.entries.length) {
      return null
    }
    return this.entries[instanceIndex] ?? null
  }

  /**
   * 按稳定 occurrence 身份读取当前 GPU 下标。
   *
   * @param occurrenceId 稳定 occurrence 身份。
   * @returns 当前 GPU 下标；不在本实例组时返回 `null`。
   */
  public resolveInstanceIndex(occurrenceId: string): number | null {
    return this.instanceIndexByOccurrenceId.get(occurrenceId) ?? null
  }

  /**
   * 读取指定 occurrence 对应的 source Mesh。
   *
   * @param occurrenceId 稳定 occurrence 身份。
   * @returns 原始显示 Mesh；不在本实例组时返回 `null`。
   */
  public resolveSourceMesh(occurrenceId: string): THREE.Mesh | null {
    const instanceIndex = this.resolveInstanceIndex(occurrenceId)
    return instanceIndex === null ? null : this.entries[instanceIndex]?.sourceMesh ?? null
  }

  /** @returns 当前快照结构代次。 */
  public getGeneration(): number {
    return this.generation
  }

  /** @returns 当前快照包含的实例数量。 */
  public getSize(): number {
    return this.entries.length
  }
}
